/**
 * ИНН должника из загруженного документа (просьба заказчика 29.10.2026).
 *
 * ИНН читается из справки ФНС о сальдо ЕНС, кредитного отчёта, выписок ФНС
 * и других документов, но служил только для сверки: если в деле ИНН пуст,
 * сверять было не с чем, и его приходилось перепечатывать. Теперь при пустом
 * ИНН предлагается внести найденный — по кнопке, как прежняя фамилия.
 *
 * Берётся только ИНН физического лица с верной контрольной суммой, и только
 * из документа на должника: если в документе стоит другое ФИО, ИНН чужой.
 * Разные ИНН в документах — без кнопки, с просьбой сверить.
 */

import { isValidInn, type CaseProfile } from '@bd/domain';

export interface DocumentSubject {
  title: string;
  subject?: { fullName?: string; inn?: string | null } | null;
}

const norm = (value: string) =>
  value.toUpperCase().replace(/Ё/g, 'Е').replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();

/** Найденные ИНН должника: ИНН → названия документов, где он стоит. */
export function debtorInnsFromDocuments(documents: DocumentSubject[], debtor: CaseProfile['debtor']): Map<string, string[]> {
  const found = new Map<string, string[]>();
  if (debtor.inn.replace(/\s/g, '')) return found;
  const name = norm([debtor.last, debtor.first, debtor.middle].filter(Boolean).join(' '));
  for (const { title, subject } of documents) {
    const inn = subject?.inn?.replace(/\s/g, '');
    if (!inn || !isValidInn(inn, 'person')) continue;
    if (subject?.fullName && name && norm(subject.fullName) !== name) continue;
    // Один файл приходит и в раздел, и в перечень приложений — название один раз
    const titles = found.get(inn) ?? [];
    found.set(inn, titles.includes(title) ? titles : [...titles, title]);
  }
  return found;
}

export function DebtorInnHint({
  documents,
  profile,
  update,
  readOnly,
}: {
  documents: DocumentSubject[];
  profile: CaseProfile;
  update: (apply: (p: CaseProfile) => CaseProfile) => void;
  readOnly?: boolean;
}) {
  const found = debtorInnsFromDocuments(documents, profile.debtor);
  if (found.size === 0) return null;

  if (found.size > 1) {
    return (
      <p className="warning">
        В документах разные ИНН должника:{' '}
        {[...found].map(([inn, titles]) => `${inn} (${titles.join('; ')})`).join(', ')} — сверьте и впишите верный в разделе «Должник».
      </p>
    );
  }

  const [[inn, titles]] = [...found] as [[string, string[]]];
  return (
    <p className="warning">
      ИНН должника в деле не указан, в документе он есть: {inn} — {titles.join('; ')}.{' '}
      <button
        type="button"
        className="link-button"
        disabled={readOnly}
        onClick={() => update((p) => (p.debtor.inn.trim() ? p : { ...p, debtor: { ...p.debtor, inn } }))}
      >
        Внести в раздел «Должник»
      </button>
    </p>
  );
}
